import { FC, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Stack, TextField } from '@mui/material'

import { InternalFormDialog } from '../../../../components/InternalFormDialog/InternalFormDialog'
import { InternalRole } from '../../types/Plans'
import { getRoleNameError, RoleFormValues, toRoleFormValues } from '../helpers/planHelpers'

type RoleEditDialogProps = {
  open: boolean
  role?: InternalRole
  roles: InternalRole[]
  isSaving?: boolean
  onClose: () => void
  onSave: (values: RoleFormValues) => void
}

export const RoleEditDialog: FC<RoleEditDialogProps> = ({ open, role, roles, isSaving, onClose, onSave }) => {
  const { t } = useTranslation()
  const { control, handleSubmit, reset, formState } = useForm<RoleFormValues>({ mode: 'onChange', defaultValues: toRoleFormValues(role) })

  useEffect(() => {
    if (open) reset(toRoleFormValues(role))
  }, [open, role, reset])

  const submit = handleSubmit(onSave)
  const canSave = formState.isValid && (!role || formState.isDirty) && !isSaving

  return (
    <InternalFormDialog
      open={open}
      title={role ? t('internal-csm:plans_edit_role') : t('internal-csm:plans_new_role')}
      onClose={onClose}
      onSave={submit}
      saveDisabled={!canSave}
      isSaving={isSaving}
    >
      <Stack spacing={2} pt={1}>
        <Controller
          name="name"
          control={control}
          rules={{
            validate: (name) => {
              if (role) return true
              const error = getRoleNameError(name, roles)
              return error ? t(`internal-csm:plans_role_name_${error}`) : true
            },
          }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              size="small"
              label={t('internal-csm:plans_role_name')}
              error={!!fieldState.error}
              helperText={fieldState.error?.message ?? (role ? t('internal-csm:plans_role_name_read_only') : undefined)}
              disabled={!!role || isSaving}
              required
            />
          )}
        />
        <Controller
          name="description"
          control={control}
          render={({ field }) => <TextField {...field} size="small" label={t('internal-csm:plans_description')} disabled={isSaving} />}
        />
      </Stack>
    </InternalFormDialog>
  )
}
